import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Instagram,
  MessageCircle,
  ArrowRight,
  Heart,
  Clock,
  Shield,
  Sparkles,
  MapPin,
  Send,
  User,
} from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/content";
import SEO, { localBusinessJsonLd, faqJsonLd } from "@/components/SEO";

const REASONS = [
  {
    title: "Reach out even if you're nervous",
    body: "Almost every woman I coach says she 'almost didn't message'. There's no wrong question, no awkward enquiry, no judgement on this side of the screen.",
    icon: Heart,
  },
  {
    title: "Personal replies, never bots",
    body: "Maya reads and replies to every single message herself — usually within 24–48 hours on weekdays.",
    icon: MessageCircle,
  },
  {
    title: "A safe, women-only space",
    body: "Everything you share is private. HerFitHub is built to feel calm, supportive and human — by design.",
    icon: Shield,
  },
];

const FAQ_ITEMS = [
  {
    q: "What's the fastest way to get a reply?",
    a: "The enquiry form below is the fastest route — it goes directly to Maya's inbox with all the context she needs. Most replies happen within 24–48 hours.",
  },
  {
    q: "I'm not ready to book — can I still message?",
    a: "Absolutely. Questions, curiosity, 'is this right for me?' messages — all welcome. There's zero obligation to book anything afterwards.",
  },
  {
    q: "Do you reply to Instagram DMs?",
    a: "Yes, but for anything coaching-related, the form below or email is faster and more thorough. Instagram is best for quick hellos and questions.",
  },
  {
    q: "Where are you based?",
    a: "Maya is based in London. In-person coaching is available across London, and online coaching for women is available worldwide.",
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page" className="bg-[#F6EFE9]">
      <SEO
        title="Contact Maya | Women's Strength Coach London — HerFitHub"
        description="Get in touch with Maya — London-based women's strength coach. Reach out for online coaching for women, postpartum strength coaching, beginner gym coaching or collaborations. Personal replies, always."
        path="/contact"
        keywords="contact women's strength coach London, women's personal trainer London contact, HerFitHub contact, Maya coach contact, women-only personal trainer London"
        jsonLd={[localBusinessJsonLd, faqJsonLd(FAQ_ITEMS)]}
      />
      <Hero />
      <Reasons />
      <ContactSection />
      <Channels />
      <FAQ />
      <FinalCTA />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="max-w-3xl mx-auto text-center fade-in">
          <p className="eyebrow mb-5" data-testid="contact-eyebrow">
            Get In Touch · Personally Read, Personally Answered
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.04] text-[#3A3A3A]">
            Let's <span className="italic text-[#E8A6B8]">connect</span>.
          </h1>
          <p className="heart-divider mt-5">♡</p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl mx-auto">
            For coaching enquiries, programs, collaborations — or simply to ask
            a question — reach out below. Every message is read by Maya
            herself, with care and zero judgement.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href="#contact-form" className="btn-pink" data-testid="hero-contact-form-cta">
              Send A Message <ArrowRight size={16} />
            </a>
            <Link to="/booking" className="btn-outline" data-testid="hero-booking-link">
              Book A Consultation
            </Link>
          </div>
          <p className="mt-8 text-[11px] sm:text-xs uppercase tracking-[0.16em] text-[#5C5C5C] flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
            <span>Replies within 24–48h</span>
            <span className="text-[#E8A6B8]">•</span>
            <span>London & online worldwide</span>
            <span className="text-[#E8A6B8]">•</span>
            <span>Women-only space</span>
          </p>
        </div>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#E8A6B8]/15 blur-3xl"
      />
    </section>
  );
}

/* ---------- REASONS ---------- */
function Reasons() {
  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-3">A Gentle Reminder</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            You're <span className="italic text-[#E8A6B8]">welcome here</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {REASONS.map((r, i) => {
            const I = r.icon;
            return (
              <article
                key={r.title}
                className="bg-white rounded-[1.75rem] p-7 border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)]"
                data-testid={`reason-${i}`}
              >
                <div className="inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 mb-5">
                  <I size={22} strokeWidth={1.5} className="text-[#E8A6B8]" />
                </div>
                <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug">
                  {r.title}
                </h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">{r.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- CONTACT SECTION (FORM + INFO) ---------- */
function ContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General enquiry",
    message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Reuse the bookings endpoint as a generic enquiry channel
      await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: "",
        coaching_option: `Contact: ${form.subject}`,
        main_goal: form.subject,
        training_experience: "",
        injuries: "",
        preferred_times: "",
        message: form.message,
      });
      setSubmitted(true);
      toast.success("Message sent. Maya will reply soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="bg-white py-20 lg:py-28 scroll-mt-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
        {/* Info column */}
        <aside className="lg:col-span-5">
          <p className="eyebrow mb-4">Drop A Message</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight">
            Say <span className="italic text-[#E8A6B8]">hi</span>
          </h2>
          <p className="mt-6 text-[#5C5C5C] leading-relaxed">
            Use the form to share a few details about what you're looking for —
            coaching, programs, postpartum support, or just a question. The more
            context you share, the better Maya can reply.
          </p>

          <div className="mt-10 space-y-5">
            <InfoLine
              icon={Mail}
              label="Email Direct"
              value={BRAND.email}
              href={`mailto:${BRAND.email}`}
              testId="info-email"
            />
            <InfoLine
              icon={Clock}
              label="Reply Time"
              value="Within 24–48 hours, Mon–Fri"
              testId="info-reply"
            />
            <InfoLine
              icon={MapPin}
              label="Location"
              value="London · Online coaching worldwide"
              testId="info-location"
            />
          </div>

          <div className="mt-10 bg-[#F6EFE9] rounded-2xl p-6 border border-[#F3C9D3]/30">
            <Sparkles size={18} className="text-[#E8A6B8] mb-2" strokeWidth={1.6} />
            <p className="font-serif-display italic text-lg text-[#3A3A3A] leading-snug">
              "Reaching out is often the hardest step. You've already started."
            </p>
          </div>
        </aside>

        {/* Form column */}
        <div className="lg:col-span-7">
          {submitted ? (
            <SuccessCard name={form.name} />
          ) : (
            <form
              onSubmit={submit}
              className="bg-[#F6EFE9] rounded-[2rem] p-6 sm:p-10 border border-[#F3C9D3]/40 space-y-5 shadow-[0_18px_44px_rgba(232,166,184,0.12)]"
              data-testid="contact-form"
              noValidate
            >
              <Field label="Your Name" icon={User} required>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="hfh-input-contact"
                  data-testid="contact-name"
                  autoComplete="name"
                />
              </Field>
              <Field label="Email Address" icon={Mail} required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="hfh-input-contact"
                  data-testid="contact-email"
                  autoComplete="email"
                />
              </Field>
              <Field label="What's this about?" icon={Sparkles}>
                <select
                  value={form.subject}
                  onChange={update("subject")}
                  className="hfh-input-contact"
                  data-testid="contact-subject"
                >
                  <option>General enquiry</option>
                  <option>Online coaching for women</option>
                  <option>Postpartum strength coaching</option>
                  <option>Beginner gym coaching</option>
                  <option>Program question</option>
                  <option>Collaboration / press</option>
                  <option>Something else</option>
                </select>
              </Field>
              <Field label="Your Message" icon={MessageCircle} required>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="hfh-input-contact resize-none"
                  placeholder="Tell Maya a little about you, your goals or your question. Take your time — there's no perfect message."
                  data-testid="contact-message"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="btn-pink w-full disabled:opacity-60"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : (
                  <>
                    <Send size={15} /> Send Message
                  </>
                )}
              </button>
              <p className="text-center text-[11px] text-[#5C5C5C] leading-relaxed">
                Your details stay private and are only used to reply to your message.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .hfh-input-contact {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #F3C9D3;
          border-radius: 0.85rem;
          padding: 0.85rem 1rem;
          font-size: 0.95rem;
          color: #3A3A3A;
          transition: border-color 200ms, box-shadow 200ms;
          font-family: inherit;
        }
        .hfh-input-contact:focus {
          outline: none;
          border-color: #E8A6B8;
          box-shadow: 0 0 0 4px rgba(232,166,184,0.18);
        }
      `}</style>
    </section>
  );
}

function Field({ label, icon: Icon, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] mb-2 inline-flex items-center gap-2">
        {Icon ? <Icon size={13} className="text-[#E8A6B8]" strokeWidth={1.8} /> : null}
        {label}
        {required ? <span className="text-[#E8A6B8]" aria-hidden>*</span> : null}
      </span>
      {children}
    </label>
  );
}

function InfoLine({ icon: Icon, label, value, href, testId }) {
  const Wrap = href ? "a" : "div";
  const props = href ? { href, "data-testid": testId } : { "data-testid": testId };
  return (
    <Wrap
      {...props}
      className="flex items-start gap-4 group"
    >
      <span className="flex-shrink-0 inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 group-hover:bg-[#E8A6B8]/40 transition-colors">
        <Icon size={18} className="text-[#E8A6B8]" strokeWidth={1.6} />
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C5C5C] font-semibold">
          {label}
        </p>
        <p className="mt-1 text-sm text-[#3A3A3A] break-words">{value}</p>
      </div>
    </Wrap>
  );
}

function SuccessCard({ name }) {
  const firstName = name?.split(" ")[0] || "there";
  return (
    <div
      className="bg-white rounded-[2rem] p-10 sm:p-14 border border-[#F3C9D3]/40 text-center shadow-[0_18px_44px_rgba(232,166,184,0.18)]"
      data-testid="contact-success"
    >
      <div className="inline-flex p-4 rounded-full bg-[#F3C9D3]/40 mb-6">
        <Heart size={28} strokeWidth={1.5} className="text-[#E8A6B8]" />
      </div>
      <p className="eyebrow mb-3">Message Received</p>
      <h3 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A] leading-tight">
        Thank you,{" "}
        <span className="italic text-[#E8A6B8]">{firstName}</span>.
      </h3>
      <p className="mt-6 text-[#5C5C5C] leading-relaxed max-w-md mx-auto">
        Your message has landed safely in Maya's inbox. She'll reply personally
        within 24–48 hours. In the meantime, take a look around — there's plenty
        to explore.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-4">
        <Link to="/programs" className="btn-pink" data-testid="contact-success-programs">
          Browse Programs <ArrowRight size={14} />
        </Link>
        <Link to="/blog" className="btn-outline" data-testid="contact-success-blog">
          Read The Journal
        </Link>
      </div>
    </div>
  );
}

/* ---------- CHANNELS ---------- */
function Channels() {
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-3">Other Ways To Reach Out</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Find me on <span className="italic text-[#E8A6B8]">your channels</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          <a
            href={`mailto:${BRAND.email}`}
            className="group bg-white rounded-[1.75rem] p-8 text-center border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)] block"
            data-testid="channel-email"
          >
            <div className="inline-flex p-4 rounded-full bg-[#F3C9D3]/40 mb-5 transition-transform duration-300 group-hover:scale-110">
              <Mail className="text-[#E8A6B8]" size={22} strokeWidth={1.5} />
            </div>
            <p className="eyebrow mb-2">Email</p>
            <p className="text-sm text-[#3A3A3A] break-words font-medium">{BRAND.email}</p>
            <p className="mt-2 text-xs text-[#5C5C5C]">Best for detailed enquiries</p>
          </a>

          {BRAND.instagram.map((ig) => (
            <a
              key={ig.handle}
              href={ig.url}
              target="_blank"
              rel="noreferrer"
              className="group bg-white rounded-[1.75rem] p-8 text-center border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)] block"
              data-testid={`channel-ig-${ig.handle}`}
            >
              <div className="inline-flex p-4 rounded-full bg-[#F3C9D3]/40 mb-5 transition-transform duration-300 group-hover:scale-110">
                <Instagram className="text-[#E8A6B8]" size={22} strokeWidth={1.5} />
              </div>
              <p className="eyebrow mb-2">Instagram</p>
              <p className="text-sm text-[#3A3A3A] font-medium">{ig.handle}</p>
              <p className="mt-2 text-xs text-[#5C5C5C]">Quick hellos & community</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="eyebrow mb-3">FAQ</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Common <span className="italic text-[#E8A6B8]">questions</span>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 px-6 sm:px-8 divide-y divide-[#F3C9D3]/30"
        >
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-0"
              data-testid={`contact-faq-${i}`}
            >
              <AccordionTrigger className="text-left text-[#3A3A3A] font-medium text-base sm:text-lg py-6 hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed pb-6">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section className="px-6 lg:px-10 pb-24 pt-4">
      <div
        className="max-w-6xl mx-auto rounded-[2rem] overflow-hidden relative text-center"
        style={{
          background:
            "linear-gradient(135deg, #E8A6B8 0%, #F3C9D3 50%, #F6EFE9 100%)",
        }}
      >
        <div className="relative px-6 sm:px-10 py-20 sm:py-24">
          <p className="eyebrow text-[#3A3A3A]/80 mb-4">Ready When You Are</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight max-w-2xl mx-auto">
            You don't have to do this <span className="italic">alone</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[#3A3A3A]/80 leading-relaxed max-w-xl mx-auto">
            One message is all it takes to start building your strength,
            confidence and capability — with someone genuinely in your corner.
          </p>
          <Link
            to="/booking"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-[#3A3A3A] hover:bg-black text-white rounded-full px-10 py-4 text-[12px] uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5 shadow-xl"
            data-testid="contact-final-cta"
          >
            Book A Consultation <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
